<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import AppHeader from '@/components/layout/AppHeader.vue';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Button } from '@/components/ui/button';
import { useTheme } from '@/composables/useTheme';
import { useApiKeyStatus, refreshApiKeyStatus } from '@/composables/useApiKeyStatus';
import { trpc } from '@/trpc';
import { Sun, Moon, Monitor } from 'lucide-vue-next';

const { theme, setTheme } = useTheme();
const route = useRoute();

const { status, ensureApiKeyStatus } = useApiKeyStatus();
const apiKeyInput = ref('');
const saving = ref(false);
const clearing = ref(false);
const error = ref<string | null>(null);

const showNeedKeyBanner = computed(() => route.query.needKey === '1' && !status.value?.hasKey);

onMounted(() => {
  ensureApiKeyStatus().catch(() => {
    // Surface a soft error; the form still works.
    error.value = 'Could not load API key status.';
  });
});

async function handleSave() {
  error.value = null;
  saving.value = true;
  try {
    await trpc.setApiKey.mutate({ apiKey: apiKeyInput.value.trim() });
    await refreshApiKeyStatus();
    apiKeyInput.value = '';
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'Could not save key.';
  } finally {
    saving.value = false;
  }
}

async function handleClear() {
  if (!confirm('Remove your API key? You will need to add one again to use the learning features.')) {
    return;
  }
  error.value = null;
  clearing.value = true;
  try {
    await trpc.clearApiKey.mutate();
    await refreshApiKeyStatus();
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'Could not clear key.';
  } finally {
    clearing.value = false;
  }
}
</script>

<template>
  <div class="min-h-screen bg-background">
    <AppHeader />

    <main class="max-w-4xl mx-auto px-4 py-8">
      <div class="space-y-6">
        <div>
          <h2 class="text-2xl font-semibold">
            Settings
          </h2>
          <p class="text-muted-foreground mt-1">
            Customize your hone.study experience
          </p>
        </div>

        <div
          v-if="showNeedKeyBanner"
          class="bg-amber-50 border border-amber-200 text-amber-900 dark:bg-amber-950/30 dark:border-amber-900/50 dark:text-amber-200 rounded-lg p-4"
        >
          Add an Anthropic API key below to start learning. hone.study uses your key to talk to Claude on your behalf.
        </div>

        <div class="bg-card rounded-lg p-6 border border-border">
          <h3 class="font-semibold text-lg mb-2">
            Anthropic API key
          </h3>
          <p class="text-sm text-muted-foreground mb-4">
            hone.study uses Claude to design curricula, tutor lessons, and grade discussions. Provide your own Anthropic API key — we store it encrypted and never share it. Get a key at
            <a
              href="https://console.anthropic.com/settings/keys"
              target="_blank"
              rel="noopener"
              class="text-primary hover:underline"
            >console.anthropic.com</a>.
          </p>

          <div
            v-if="status?.hasKey"
            class="space-y-3"
          >
            <div class="flex items-center gap-2 text-sm">
              <span class="text-muted-foreground">On file:</span>
              <span class="font-mono">sk-ant-…{{ status.lastFour }}</span>
            </div>
            <div class="flex gap-2">
              <Button
                variant="outline"
                :disabled="clearing"
                @click="handleClear"
              >
                {{ clearing ? 'Removing…' : 'Remove key' }}
              </Button>
            </div>
            <p class="text-xs text-muted-foreground">
              To rotate, remove the current key and add a new one.
            </p>
          </div>

          <form
            v-else
            class="space-y-3"
            @submit.prevent="handleSave"
          >
            <input
              v-model="apiKeyInput"
              type="password"
              placeholder="sk-ant-..."
              autocomplete="off"
              spellcheck="false"
              required
              class="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground font-mono focus:outline-none focus:ring-2 focus:ring-ring"
            >
            <Button
              type="submit"
              :disabled="saving || !apiKeyInput.trim()"
            >
              {{ saving ? 'Verifying…' : 'Save key' }}
            </Button>
            <p class="text-xs text-muted-foreground">
              We verify the key with Anthropic before saving.
            </p>
          </form>

          <div
            v-if="error"
            class="text-destructive text-sm mt-3"
          >
            {{ error }}
          </div>
        </div>

        <div class="bg-card rounded-lg p-6 border border-border">
          <h3 class="font-semibold text-lg mb-2">
            Appearance
          </h3>
          <p class="text-sm text-muted-foreground mb-4">
            Choose how hone.study looks to you.
          </p>
          <ToggleGroup
            type="single"
            :model-value="theme"
            variant="outline"
            @update:model-value="(val) => val && setTheme(val as 'light' | 'dark' | 'system')"
          >
            <ToggleGroupItem
              value="light"
              class="gap-2 px-3"
            >
              <Sun class="size-4" />
              Light
            </ToggleGroupItem>
            <ToggleGroupItem
              value="dark"
              class="gap-2 px-3"
            >
              <Moon class="size-4" />
              Dark
            </ToggleGroupItem>
            <ToggleGroupItem
              value="system"
              class="gap-2 px-3"
            >
              <Monitor class="size-4" />
              System
            </ToggleGroupItem>
          </ToggleGroup>
        </div>
      </div>
    </main>
  </div>
</template>
